import { Injectable, signal, computed } from '@angular/core';
import { CaseFormEntity } from '../../domain/entities/case-form.entity';
import { CaseFormDetailEntity } from '../../domain/entities/case-form-detail.entity';
import { SensitivityCategoryEntity } from '../../domain/entities/sensitivity-category.entity';
import { FieldConstraintTypeResponseDto, FieldTypeResponseDto } from '../../infrastructure/dtos/field-constraint-type-response.dto';

/**
 * Case Form State
 * Manages case form state using Angular signals
 */
@Injectable({ providedIn: 'root' })
export class CaseFormState {
  // Private writable signals
  private readonly _caseForms = signal<CaseFormEntity[]>([]);
  private readonly _caseFormsCount = signal<number>(0);
  private readonly _caseFormSelectedToEdit = signal<CaseFormDetailEntity | null>(null);
  private readonly _fieldTypes = signal<FieldTypeResponseDto[]>([]);
  private readonly _fieldConstraintTypes = signal<FieldConstraintTypeResponseDto[]>([]);
  private readonly _sensitivityCategories = signal<SensitivityCategoryEntity[]>([]);
  private readonly _loadingCaseForms = signal(false);
  private readonly _loadingCaseForm = signal(false);
  private readonly _loadingFieldConstraintTypes = signal(false);
  private readonly _loadingSensitivityCategories = signal(false);
  private readonly _errorCaseForms = signal<string | null>(null);
  private readonly _errorCaseForm = signal<string | null>(null);
  private readonly _savingCaseForm = signal(false);

  // Public readonly signals
  public readonly caseForms = this._caseForms.asReadonly();
  public readonly caseFormsCount = this._caseFormsCount.asReadonly();
  public readonly caseFormSelectedToEdit = this._caseFormSelectedToEdit.asReadonly();
  public readonly fieldTypes = this._fieldTypes.asReadonly();
  public readonly fieldConstraintTypes = this._fieldConstraintTypes.asReadonly();
  public readonly sensitivityCategories = this._sensitivityCategories.asReadonly();
  public readonly loadingCaseForms = this._loadingCaseForms.asReadonly();
  public readonly loadingCaseForm = this._loadingCaseForm.asReadonly();
  public readonly loadingFieldConstraintTypes = this._loadingFieldConstraintTypes.asReadonly();
  public readonly loadingSensitivityCategories = this._loadingSensitivityCategories.asReadonly();
  public readonly errorCaseForms = this._errorCaseForms.asReadonly();
  public readonly errorCaseForm = this._errorCaseForm.asReadonly();
  public readonly savingCaseForm = this._savingCaseForm.asReadonly();

  // Computed signals
  public readonly hasCaseForms = computed(() => this._caseForms().length > 0);

  /**
   * Sets the list of case forms
   */
  public setCaseForms(caseForms: CaseFormEntity[]): void {
    this._caseForms.set(caseForms);
  }

  /**
   * Sets the total count of case forms
   */
  public setCaseFormsCount(count: number): void {
    this._caseFormsCount.set(count);
  }

  /**
   * Sets the case form selected to edit
   */
  public setCaseFormSelectedToEdit(caseForm: CaseFormDetailEntity | null): void {
    this._caseFormSelectedToEdit.set(caseForm);
  }

  /**
   * Sets the loading state for case forms (list)
   */
  public setLoadingCaseForms(loading: boolean): void {
    this._loadingCaseForms.set(loading);
  }

  /**
   * Sets the loading state for a single case form (detail)
   */
  public setLoadingCaseForm(loading: boolean): void {
    this._loadingCaseForm.set(loading);
  }

  /**
   * Sets an error message for case forms (list)
   */
  public setErrorCaseForms(error: string | null): void {
    this._errorCaseForms.set(error);
  }

  /**
   * Sets an error message for a single case form (detail)
   */
  public setErrorCaseForm(error: string | null): void {
    this._errorCaseForm.set(error);
  }

  /**
   * Clears the error for case forms (list)
   */
  public clearErrorCaseForms(): void {
    this._errorCaseForms.set(null);
  }

  /**
   * Clears the error for a single case form (detail)
   */
  public clearErrorCaseForm(): void {
    this._errorCaseForm.set(null);
  }

  /**
   * Sets the saving state for a case form
   */
  public setSavingCaseForm(saving: boolean): void {
    this._savingCaseForm.set(saving);
  }

  /**
   * Sets the field types (with operators and constraints)
   */
  public setFieldTypes(fieldTypes: FieldTypeResponseDto[]): void {
    this._fieldTypes.set(fieldTypes);
  }

  /**
   * Sets the field constraint types
   */
  public setFieldConstraintTypes(constraints: FieldConstraintTypeResponseDto[]): void {
    this._fieldConstraintTypes.set(constraints);
  }

  /**
   * Sets the loading state for field constraint types
   */
  public setLoadingFieldConstraintTypes(loading: boolean): void {
    this._loadingFieldConstraintTypes.set(loading);
  }

  /**
   * Sets the available sensitivity categories used to classify form fields.
   */
  public setSensitivityCategories(categories: SensitivityCategoryEntity[]): void {
    this._sensitivityCategories.set(categories);
  }

  /**
   * Sets the loading state for sensitivity categories.
   */
  public setLoadingSensitivityCategories(loading: boolean): void {
    this._loadingSensitivityCategories.set(loading);
  }

  /**
   * Removes a case form from the list by ID
   */
  public removeCaseForm(id: string): void {
    this._caseForms.update((forms) => forms.filter((form) => form.id !== id));
  }

  public updateCaseFormStatus(id: string, status: CaseFormEntity['status']): void {
    this._caseForms.update((forms) =>
      forms.map((form) => (form.id === id ? { ...form, status, statusName: status } : form))
    );
  }

  /**
   * Clears all case form state
   */
  public clear(): void {
    this._caseForms.set([]);
    this._caseFormsCount.set(0);
    this._caseFormSelectedToEdit.set(null);
    this._fieldConstraintTypes.set([]);
    this._sensitivityCategories.set([]);
    this._loadingCaseForms.set(false);
    this._loadingCaseForm.set(false);
    this._loadingFieldConstraintTypes.set(false);
    this._loadingSensitivityCategories.set(false);
    this._errorCaseForms.set(null);
    this._errorCaseForm.set(null);
    this._savingCaseForm.set(false);
  }
}
